/* ==========================================================================
   OrangeWear — recently-viewed.js
   <recently-viewed>: remembers viewed product handles in localStorage and
   renders their cards through sections/product-card-render.liquid.
   ========================================================================== */
(() => {
  if (customElements.get('recently-viewed')) return;

  const STORAGE_KEY = 'orangewear:recently-viewed';
  const MAX_STORED = 12;
  const HANDLE_PATTERN = /^[^\s/?#]{1,255}$/;

  const readHandles = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((handle, index) => {
        return typeof handle === 'string' && HANDLE_PATTERN.test(handle) && parsed.indexOf(handle) === index;
      });
    } catch (error) {
      return [];
    }
  };

  const writeHandles = (handles) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
    } catch (error) {
      /* Storage full or blocked: nothing to remember. */
    }
  };

  const productUrl = (handle) => {
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    return `${base}products/${encodeURIComponent(handle)}?section_id=product-card-render`;
  };

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-recently-viewed-list]');
      this.notice = this.querySelector('[data-recently-viewed-notice]');

      const current = (this.dataset.productHandle || '').trim();
      let handles = readHandles();
      if (current && HANDLE_PATTERN.test(current)) {
        handles = [current, ...handles.filter((handle) => handle !== current)].slice(0, MAX_STORED);
        writeHandles(handles);
      }

      const limit = Number.parseInt(this.dataset.limit, 10) || 4;
      this.handles = handles.filter((handle) => handle !== current).slice(0, limit);

      if (!this.list || !this.handles.length) {
        this.finish(0);
        return;
      }

      const target = this.parentElement || this;
      if (!('IntersectionObserver' in window)) {
        this.render();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.render();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async render() {
      if (this.rendering) return;
      this.rendering = true;

      const cards = await Promise.all(this.handles.map((handle) => this.fetchCard(handle)));
      const fragment = document.createDocumentFragment();
      let count = 0;
      cards.forEach((card) => {
        if (!card) return;
        const item = document.createElement('li');
        item.className = 'product-rail__item';
        item.appendChild(document.importNode(card, true));
        fragment.appendChild(item);
        count += 1;
      });

      this.list.replaceChildren(fragment);
      this.finish(count);
    }

    async fetchCard(handle) {
      try {
        const response = await fetch(productUrl(handle), { headers: { Accept: 'text/html' } });
        if (!response.ok) {
          if (response.status === 404) this.forget(handle);
          return null;
        }
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('#ProductCardRender .product-card');
      } catch (error) {
        return null;
      }
    }

    forget(handle) {
      writeHandles(readHandles().filter((stored) => stored !== handle));
    }

    finish(count) {
      const designMode = Boolean(window.Shopify && window.Shopify.designMode);
      if (this.notice) this.notice.hidden = count > 0;
      this.hidden = count === 0 && !designMode;
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
})();
